import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAuditLogs } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Activity, 
  Search,
  Filter,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Home,
  Building2,
  Share2,
  Users,
  Lock,
  CheckCircle2,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const AuditPage = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 50;

  const loadLogs = async (reset = false) => {
    setLoading(true);
    try {
      const skip = reset ? 0 : page * PAGE_SIZE;
      const data = await getAuditLogs({
        entity_type: entityTypeFilter && entityTypeFilter !== 'all' ? entityTypeFilter : undefined,
        limit: PAGE_SIZE,
        skip
      });
      
      if (reset) {
        setLogs(data);
        setPage(0);
      } else {
        setLogs(data);
      }
      
      setHasMore(data.length === PAGE_SIZE);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs(true);
  }, [entityTypeFilter]);

  useEffect(() => {
    if (page > 0) {
      loadLogs(false);
    }
  }, [page]);

  const getActionIcon = (action) => {
    if (action.includes('block')) return <Clock className="w-4 h-4" />;
    if (action.includes('book')) return <CheckCircle2 className="w-4 h-4" />;
    if (action.includes('available')) return <Lock className="w-4 h-4" />;
    if (action.includes('create')) return <Building2 className="w-4 h-4" />;
    if (action.includes('update')) return <Activity className="w-4 h-4" />;
    if (action.includes('delete')) return <AlertTriangle className="w-4 h-4" />;
    return <Activity className="w-4 h-4" />;
  };

  const getActionColor = (action) => {
    if (action.includes('block')) return 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400';
    if (action.includes('book')) return 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-400';
    if (action.includes('available')) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400';
    if (action.includes('create')) return 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400';
    if (action.includes('delete')) return 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400';
    return 'bg-muted text-muted-foreground';
  };

  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case 'unit': return <Home className="w-4 h-4" />;
      case 'project': return <Building2 className="w-4 h-4" />;
      case 'channel': return <Share2 className="w-4 h-4" />;
      case 'user': return <Users className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const filteredLogs = logs.filter(log => 
    log.details?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.action?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.entity_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-in" data-testid="audit-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Audit Log</h1>
          <p className="text-muted-foreground mt-1">
            Complete history of all inventory actions
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-logs"
              />
            </div>
            
            <div className="w-[200px]">
              <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
                <SelectTrigger data-testid="entity-type-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Entities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  <SelectItem value="unit">Units</SelectItem>
                  <SelectItem value="project">Projects</SelectItem>
                  <SelectItem value="tower">Towers</SelectItem>
                  <SelectItem value="channel">Channels</SelectItem>
                  <SelectItem value="user">Users</SelectItem>
                  <SelectItem value="developer">Developers</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      {loading && logs.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredLogs.length === 0 ? (
        <Card className="p-12 text-center">
          <Activity className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No logs found</h3>
          <p className="text-muted-foreground">
            {searchQuery || entityTypeFilter ? 'Try different filters' : 'Actions will appear here'}
          </p>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Action</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map((log) => (
                <TableRow key={log.log_id} data-testid={`log-row-${log.log_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getActionColor(log.action)}`}>
                        {getActionIcon(log.action)}
                      </div>
                      <span className="font-medium capitalize">
                        {log.action.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getEntityIcon(log.entity_type)}
                      <div>
                        <p className="font-medium capitalize">{log.entity_type}</p>
                        <p className="text-xs text-muted-foreground font-mono">{log.entity_id}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{log.user_name}</p>
                      <Badge variant="secondary" className="text-xs capitalize">{log.user_role}</Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm max-w-[300px] truncate">{log.details || '-'}</p>
                    {log.before_state && log.after_state && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {log.before_state.status} → {log.after_state.status}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-sm">{formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}</p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(log.timestamp), 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {filteredLogs.length} logs
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={!hasMore || loading}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AuditPage;
